import React, { lazy, Suspense } from 'react';
import {
  Switch,
  Route,
  useRouteMatch,
  Redirect,
  useLocation,
} from 'react-router-dom';
import AdminPermissions from './pages/admin-dashboard/index.js';
import LifelineDashboard from './pages/lifeline/LifelineDashboard/LifelineDashboard';
import {
  getCurrentUserEmail,
  getCurrentUserId,
  hasCookieToken,
} from './utils/CookieToken';
import { Loader } from './UiKit';
import {
  handleThirdPartyCookie,
  redirectToLogin,
} from './utils/UtilFunctions.js';
import { isUserLogged } from './App.js';
import Login from './pages/login/Login.jsx';
import WatermarkLayer from './components/ComponentsV1/Watermark/Watermark.jsx';

const SmartplugDashboard = lazy(() =>
  import('./pages/lifeline/SmartplugDashboard/SmartplugDashboard'),
);

const Permissions = lazy(() =>
  import('./pages/permission-dashboard/index.jsx'),
);

const HandoverDashboard = lazy(() =>
  import('./pages/lifeline/HandoverDashboard/HandoverDashboard'),
);

const BulkUploadsDashboard = lazy(() => import('./pages/bulk-uploads'));

const PolicyCenter = lazy(() => import('./pages/policy-center/index.jsx'));

function LifelineRoutes() {
  return (
    <Switch>
      <Route path='/login'>
        {hasCookieToken ? <Redirect to='/lifeline' /> : <Login />}
      </Route>

      <Route path={'/bulk-uploads'}>
        <BulkUploadsDashboard />
      </Route>

      <Route
        path={[`/permissions-dashboard/:tabSlug`, `/permissions-dashboard`]}
      >
        <Permissions />
      </Route>

      <Route path={`/lifeline/smartplugs`}>
        <SmartPlugRoutes />
      </Route>

      <Route path={`/policy-center`}>
        <PolicyCenter />
      </Route>

      <Route path={`/lifeline`}>
        <LifelineDashboard />
      </Route>

      <Route path={[`/admin-dashboard/:tabSlug`, `/admin-dashboard`]}>
        <AdminPermissions />
      </Route>

      <Redirect to='/lifeline' />
    </Switch>
  );
}

function SmartPlugRoutes() {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route path={`${path}/handover`}>
        <HandoverDashboard />
      </Route>
      <Route path={[`${path}/o2-india-lifeline`, `${path}/delivery-partner`]}>
        <SmartplugDashboard />
      </Route>
      <Redirect to={`${path}/o2-india-lifeline`} />
    </Switch>
  );
}

export default function Routes() {
  const { search, pathname } = useLocation();

  const isAuthenticated = hasCookieToken;
  const notAuthenticated = !isAuthenticated;

  const isSmartplugUrl = pathname.includes('smartplugs');
  const isLoginUrl = pathname.includes('login');
  const isLifelineUrl = !isSmartplugUrl;

  const redirectUrl = encodeURIComponent(`${pathname}${search}`);

  if (notAuthenticated && isLifelineUrl && !isLoginUrl) {
    sessionStorage.setItem(isUserLogged, false);
    redirectToLogin(redirectUrl);
    return (
      <div className='tw-w-full tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-8'>
        <Loader />
        <p className='tw-text-700'>Dashboard Loading ...</p>
      </div>
    );
  }

  if (notAuthenticated && isSmartplugUrl) {
    sessionStorage.setItem(isUserLogged, false);
    handleThirdPartyCookie();
  }

  return (
    <Suspense fallback={<span>Loading...</span>}>
      {/* Lifeline user watermark */}
      {hasCookieToken && (
        <WatermarkLayer
          userEmail={getCurrentUserEmail()}
          userId={getCurrentUserId()}
        />
      )}

      <Route path='/'>
        <LifelineRoutes />
      </Route>
    </Suspense>
  );
}
